import React, { useState } from 'react';
import { Check, AlertCircle } from 'react-feather';
import SatisfactionCheck from './SatisfactionCheck';
import SupportTicketForm from './SupportTicketForm';
import FeedbackButtons from './FeedbackButtons';
import TeachingForm from './TeachingForm';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  metadata?: {
    confidence?: number;
    responseTime?: number;
    sources?: any[];
    responseId?: string;
    conversationId?: string;
  };
}

interface SatisfactionAndEscalationProps {
  responseId: string;
  conversationId: string;
  messageId: string;
  originalQuestion: string;
  originalAnswer: string;
  originalResponse: string;
  conversationHistory: Message[];
  isAuthenticated?: boolean;
  canUseTeaching?: boolean;
  authenticatedUser?: { id: string; name: string; email: string; role: string } | null;
  showEscalationDialog?: boolean; // For low-confidence: show escalation immediately
  onComplete?: () => void;
}

/**
 * Integrated component that handles satisfaction check → escalation flow
 * Like a complete communication protocol from signal flags to distress beacon
 */
const SatisfactionAndEscalation: React.FC<SatisfactionAndEscalationProps> = ({
  responseId,
  conversationId,
  messageId,
  originalQuestion,
  originalAnswer,
  originalResponse,
  conversationHistory,
  isAuthenticated = false,
  canUseTeaching = false,
  authenticatedUser = null,
  showEscalationDialog = false,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState<'satisfaction' | 'escalation' | 'teaching' | 'complete'>(
    showEscalationDialog ? 'escalation' : 'satisfaction'
  );
  const [satisfactionResponse, setSatisfactionResponse] = useState<string | null>(null);
  const [escalationMessage, setEscalationMessage] = useState<string>(
    showEscalationDialog
      ? "I'm sorry I couldn't answer your question properly. Would you like me to escalate this to a human support agent who can help you better?"
      : ''
  );
  const [showLegacyFeedback, setShowLegacyFeedback] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [showTeachingForm, setShowTeachingForm] = useState(false);
  const [_teachingCompleted, setTeachingCompleted] = useState(false);
  void _teachingCompleted; // Suppress unused warning - state tracked for potential UI updates
  const [cameFromTeaching, setCameFromTeaching] = useState(false);

  /**
   * Handle satisfaction response from user
   * Like receiving a signal from the lookout
   */
  const handleSatisfactionResponse = async (satisfaction: string) => {
    try {
      setSatisfactionResponse(satisfaction);

      // Record satisfaction with API
      const response = await fetch('/api/chat/satisfaction', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          responseId,
          conversationId,
          messageId,
          satisfaction,
          originalResponse,
          confidence: 1.0
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to record satisfaction');
      }

      const data = await response.json();

      if (data.data.shouldOfferEscalation) {
        // Show escalation step
        setEscalationMessage(getEscalationMessage(satisfaction));
        setCurrentStep('escalation');
      } else {
        // Show completion message and optionally legacy feedback
        setCurrentStep('complete');
        
        // For satisfied users, we might want to show legacy feedback after a delay
        if (satisfaction === 'yes' || satisfaction === 'skip') {
          setTimeout(() => {
            setShowLegacyFeedback(true);
          }, 2000);
        }
        
        if (onComplete) {
          onComplete();
        }
      }

    } catch (error) {
      console.error('Failed to record satisfaction:', error);
      // Continue with flow even if API call fails
      if (satisfaction === 'partial' || satisfaction === 'no') {
        setEscalationMessage(getEscalationMessage(satisfaction));
        setCurrentStep('escalation');
      } else {
        setCurrentStep('complete');
        if (onComplete) {
          onComplete();
        }
      }
    }
  };

  /**
   * Get appropriate escalation message based on satisfaction level
   */
  const getEscalationMessage = (satisfaction: string): string => {
    switch (satisfaction) {
      case 'partial':
        return "It looks like I partially answered your question. Would you like me to help you create a support ticket with our help desk?";
      case 'no':
        return "I'm sorry I couldn't answer your question properly. Would you like me to escalate this to a human support agent who can help you better?";
      default:
        return "Would you like me to help you create a support ticket with our help desk?";
    }
  };

  /**
   * Handle user declining escalation
   */
  const handleDeclineEscalation = () => {
    setCurrentStep('complete');
    setShowLegacyFeedback(true); // Show legacy feedback as fallback
    if (onComplete) {
      onComplete();
    }
  };

  /**
   * Handle user selecting teaching option
   * Now shows form directly without intermediate prompt
   */
  const handleTeachingSelected = () => {
    setCameFromTeaching(true);
    setShowTeachingForm(true);
    setCurrentStep('teaching');
  };

  /**
   * Handle teaching form completion
   */
  const handleTeachingComplete = () => {
    setTeachingCompleted(true);
    setCameFromTeaching(true);
    setCurrentStep('complete');
    if (onComplete) {
      onComplete();
    }
  };

  /**
   * Handle teaching form submission
   * The TeachingForm handles the preview/approval workflow internally,
   * so we just need to complete the flow when it's done
   */
  const handleTeachingSubmit = (teachingData: any) => {
    // TeachingForm has already handled the API calls (preview -> approve)
    // teachingData contains the final created teaching result
    
    console.log('Teaching successfully created:', teachingData);
    
    // Close teaching form and complete the flow
    setShowTeachingForm(false);
    handleTeachingComplete();
    
    // Scroll to bottom to show the success message
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };

  /**
   * Handle successful ticket creation
   */
  const handleTicketCreated = (_ticketInfo: any) => {
    // ticketInfo available if we need to display ticket details in future
    setCurrentStep('complete');
    if (onComplete) {
      onComplete();
    }
  };

  /**
   * Convert chat messages to escalation format
   */
  const formatConversationForEscalation = () => {
    return conversationHistory.map(msg => ({
      type: msg.type as 'user' | 'assistant',
      content: msg.content,
      timestamp: msg.timestamp.toISOString(),
      messageId: msg.id,
      metadata: msg.metadata
    }));
  };

  // Render satisfaction check step
  if (currentStep === 'satisfaction') {
    return (
      <div className="satisfaction-escalation-container">
        <SatisfactionCheck
          responseId={responseId}
          conversationId={conversationId}
          messageId={messageId}
          originalResponse={originalResponse}
          onSatisfactionResponse={handleSatisfactionResponse}
          isAuthenticated={isAuthenticated}
          canUseTeaching={canUseTeaching}
          onTeachingSelected={handleTeachingSelected}
        />
      </div>
    );
  }

  // Render escalation step
  if (currentStep === 'escalation') {
    return (
      <div className="satisfaction-escalation-container">
        <div className="escalation-prompt mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="escalation-message flex items-start gap-3">
            <AlertCircle size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-900">{escalationMessage}</p>
          </div>
          
          <div className="escalation-actions flex gap-3 mt-4">
            <button
              onClick={() => setShowTicketForm(true)}
              className="btn btn-primary px-4 py-2 bg-eddy-blue text-white rounded-lg hover:bg-eddy-blue/90 transition-colors text-sm font-medium flex items-center gap-2 shadow-sm hover:shadow-md"
            >
              <Check size={16} />
              Yes, contact support
            </button>
            
            <button
              onClick={handleDeclineEscalation}
              className="px-4 py-2 rounded-lg text-sm font-semibold shadow-sm hover:shadow-md transition-colors"
              style={{
                backgroundColor: '#f3f4f6',
                borderWidth: '2px',
                borderColor: '#6b7280',
                color: '#1f2937'
              }}
            >
              No, thanks
            </button>
          </div>
        </div>

        {/* Support ticket form - show as popup overlay when user confirms escalation */}
        {showTicketForm && (
          <SupportTicketForm
            conversationHistory={formatConversationForEscalation()}
            originalQuestion={originalQuestion}
            responseId={responseId}
            conversationId={conversationId}
            onTicketCreated={handleTicketCreated}
            onCancel={() => setShowTicketForm(false)}
            authenticatedUser={authenticatedUser}
          />
        )}
      </div>
    );
  }

  // Render teaching step
  if (currentStep === 'teaching') {
    return (
      <div className="satisfaction-escalation-container">
        {/* Teaching form - now shows directly without intermediate prompt */}
        {showTeachingForm && authenticatedUser && (
          <TeachingForm
            originalQuestion={originalQuestion}
            originalAnswer={originalAnswer}
            conversationHistory={conversationHistory}
            authenticatedUser={authenticatedUser}
            onSubmit={handleTeachingSubmit}
            onCancel={() => {
              setShowTeachingForm(false);
              setCameFromTeaching(true);
              setCurrentStep('complete');
            }}
          />
        )}
      </div>
    );
  }

  // Render completion step
  if (currentStep === 'complete') {
    let completionMessage: string;
    
    if (cameFromTeaching) {
      // Special message when user used the teaching workflow (completed or cancelled)
      completionMessage = 'Teaching successfully created.';
    } else {
      // Regular completion messages for satisfaction workflow
      completionMessage = satisfactionResponse === 'yes' 
        ? "Great! I'm glad I could help you. Is there anything else you'd like to know?"
        : satisfactionResponse === 'skip'
        ? "Answer satisfaction response skipped. Feel free to leave feedback on your experience below."
        : "Thank you for your feedback!";
    }

    return (
      <div className="satisfaction-escalation-container">
        <div className="completion-message flex items-center gap-2 p-3 bg-eddy-green/10 border border-eddy-green/20 rounded-lg mt-4">
          <Check size={16} className="text-eddy-green" />
          <p className="text-sm text-eddy-green">{completionMessage}</p>
        </div>

        {/* Show legacy feedback if needed */}
        {showLegacyFeedback && (
          <div className="legacy-feedback-container mt-4">
            <div className="text-xs text-gray-500 mb-2">
              Additional feedback (optional):
            </div>
            <FeedbackButtons
              responseId={responseId}
              conversationId={conversationId}
              messageId={messageId}
              originalQuestion={originalQuestion}
              originalAnswer={originalAnswer}
              originalSources={conversationHistory[conversationHistory.length - 1]?.metadata?.sources || []}
              conversationHistory={conversationHistory}
            />
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default SatisfactionAndEscalation;